import React, { useState } from 'react';

import {
  Box,
  Button,
  CheckboxGroup,
  FormLayout,
  Link,
  SimpleCheckbox,
  Text,
  TextInput,
  Toast,
  ValidatedTextInput,
} from '@opendoor/bricks-next';
import { globalObservability } from '@opendoor/observability/slim';

import { FooterRoutes, LandingPageRoutes } from 'components/shared/constants';

const Sentry = globalObservability.getSentryClient();
const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/19123831/277bvhz/';
const MAX_RETRIES = 3;
const RETRY_DELAY = 500;

const KeyAgentSignUp: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: undefined,
    phoneNumber: undefined,
    brokerage: '',
    mlsId: '',
    interests: [],
    agreeToMarketing: false,
    mlsName: '',
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [fieldValidation, setFieldValidation] = useState<
    Record<string, 'valid' | 'invalid' | 'unknown'>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showInputErrorToast, setShowInputErrorToast] = useState(false);

  const validateName = (name: string): boolean => {
    const cleanedName = name.trim().replace(/[^a-zA-Z\s]/g, '');
    return cleanedName.length >= 1;
  };

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const submitFormWithRetry = async (retryCount = 0): Promise<Response> => {
    try {
      const response = await fetch(ZAPIER_WEBHOOK_URL, {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          createdAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const error = new Error('Failed to submit key agent sign upform');
        Sentry.withScope?.((scope) => {
          scope.setExtras({
            formData,
            responseStatus: response.status,
            responseStatusText: response.statusText,
          });
          Sentry.captureException?.(error);
        });

        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          return submitFormWithRetry(retryCount + 1);
        }
        throw error;
      }

      return response;
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return submitFormWithRetry(retryCount + 1);
      }
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newFieldErrors: Record<string, boolean> = {
      firstName: !formData.firstName || !validateName(formData.firstName),
      lastName: !formData.lastName || !validateName(formData.lastName),
      email: !formData.email || fieldValidation.email !== 'valid',
      phone: Boolean(formData.phoneNumber) && fieldValidation.phone !== 'valid',
    };
    setFieldErrors(newFieldErrors);

    const hasErrors = newFieldErrors.fullName || newFieldErrors.email || newFieldErrors.phone;
    if (hasErrors) {
      setShowInputErrorToast(true);
      const el = document.querySelector<HTMLInputElement>('input[aria-invalid="true"]');
      if (el) {
        el.focus();
        el.scrollIntoView({ block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      await submitFormWithRetry();
      setShowSuccessToast(true);
      setTimeout(() => {
        window.location.assign(LandingPageRoutes.AGENTS);
      }, 4000);
    } catch (error) {
      setShowErrorToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormLayout formId="key-agent-signup-form" onSubmit={handleSubmit}>
      <FormLayout.ScrollContainer>
        <FormLayout.TopNav
          navLabel="Opendoor Key Agent™"
          textButtonProps={{
            label: 'Exit',
            href: LandingPageRoutes.AGENTS,
            analyticsName: 'key-agent-signup-exit',
          }}
        />
        <FormLayout.FormContent animationKey={1} skipEnterAnimation={true}>
          <FormLayout.Title
            title="Sign up to be an Opendoor Key Agent™"
            subhead="Enter your details to stay connected with us and access free Silver Tier membership"
          />
          <FormLayout.QuestionContainer>
            <TextInput
              title="First name"
              inputMode="text"
              required
              id="firstName"
              $largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-first-name"
              value={formData.firstName}
              onChangeText={(value) => handleInputChange('firstName', value)}
              errorText={
                fieldErrors.firstName ? 'Please enter your first name to continue' : undefined
              }
            />
            <TextInput
              title="Last name"
              inputMode="text"
              required
              id="lastName"
              $largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-last-name"
              value={formData.lastName}
              onChangeText={(value) => handleInputChange('lastName', value)}
              errorText={
                fieldErrors.lastName ? 'Please enter your last name to continue' : undefined
              }
            />
            <ValidatedTextInput
              title="Email address"
              required
              format="emailAddress"
              id="email"
              $largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-email"
              value={formData.email}
              onChangeText={(value) => handleInputChange('email', value)}
              onValidationChange={(state) =>
                setFieldValidation({ ...fieldValidation, email: state })
              }
              errorText={
                fieldErrors.email
                  ? 'Please enter a valid email address to sign up, should include an @ and domain (ex. .com).'
                  : undefined
              }
            />
            <ValidatedTextInput
              title="Phone number"
              id="phone"
              format="telephoneNumber"
              $largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-phone"
              value={formData.phoneNumber}
              onChangeText={(value) => handleInputChange('phoneNumber', value)}
              onValidationChange={(state) =>
                setFieldValidation({ ...fieldValidation, phone: state })
              }
            />
            <TextInput
              title="Brokerage name"
              supportText="Share which brokerage you hang your license with"
              id="brokerage"
              $largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-brokerage"
              value={formData.brokerage}
              onChangeText={(value) => handleInputChange('brokerage', value)}
            />
            <TextInput
              title="MLS ID"
              id="mlsId"
              $largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-mls-id"
              value={formData.mlsId}
              onChangeText={(value) => handleInputChange('mlsId', value)}
            />
            <TextInput
              title="MLS Name"
              id="mlsName"
              $largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-mls-name"
              value={formData.mlsName}
              onChangeText={(value) => handleInputChange('mlsName', value)}
            />
            <CheckboxGroup
              $group-formLayout-largerThanMD={{
                size: 'large',
              }}
              size="medium"
              analyticsName="key-agent-signup-checkbox-group"
              id="interests"
              options={[
                { label: 'Cash offers for your clients', value: 'cash_offers' },
                { label: 'Receiving leads from Opendoor', value: 'leads' },
                { label: 'Toolkits and resources', value: 'toolkits' },
                { label: 'Key agent events', value: 'events' },
              ]}
              supportText="Select all that apply"
              title="What about the Opendoor Key Agent™ program interests you?"
              onPress={(interests) => handleInputChange('interests', interests)}
            />
            <SimpleCheckbox
              id="marketing-consent"
              analyticsName="key-agent-signup-consent"
              checked={formData.agreeToMarketing}
              onPress={(_, checked) => {
                handleInputChange('agreeToMarketing', checked);
              }}
              label="I consent to receive marketing calls and texts from Opendoor and its affiliates to the number provided. I understand that I may opt out at any time and consent is not a condition of purchase."
              value="marketing-consent"
              size="small"
            />
            <Text typography="$bodySmall" tag="span" color="$contentSecondary">
              By clicking "Sign up" I agree to Opendoor’s{' '}
              <Link
                analyticsName="terms-of-service-key-agent-signup"
                href="/terms"
                size="small"
                variant="primary"
              >
                terms of service
              </Link>{' '}
              and{' '}
              <Link
                analyticsName="privacy-policy-key-agent-signup"
                href="/privacy"
                size="small"
                variant="primary"
              >
                privacy policy
              </Link>
            </Text>
            <Button
              variant="primary"
              label="Sign up"
              size="large"
              analyticsName="key-agent-signup-submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              alignSelf="flex-end"
              $smallerThanMD={{
                alignSelf: 'stretch',
              }}
            />
            <Box mb="$12x">
              <FormLayout.LegalCopy
                links={[
                  {
                    text: 'Do Not Sell My Info',
                    analyticsName: 'key-agent-do-not-sell-my-info',
                    href: FooterRoutes.PRIVACY_REQUEST,
                  },
                  {
                    text: 'Notice at collection',
                    analyticsName: 'key-agent-notice-at-collection',
                    href: FooterRoutes.CCPA,
                  },
                  {
                    text: 'Terms of Service',
                    analyticsName: 'key-agent-terms-of-service',
                    href: FooterRoutes.TERMS_OF_SERVICE,
                  },

                  {
                    text: 'Privacy Policy',
                    analyticsName: 'key-agent-privacy-policy',
                    href: FooterRoutes.PRIVACY_POLICY,
                  },
                ]}
              />
            </Box>
          </FormLayout.QuestionContainer>
        </FormLayout.FormContent>
      </FormLayout.ScrollContainer>
      <Toast
        message="Thanks for submitting your information! We'll be in touch with more info in the coming months"
        isOpen={showSuccessToast}
        isSuccess={true}
        onClose={() => setShowSuccessToast(false)}
        duration={4000}
      />
      <Toast
        message="Sorry, there was an error submitting your form. Please try again."
        isOpen={showErrorToast}
        hasError={true}
        onClose={() => setShowErrorToast(false)}
        duration={4000}
      />
      <Toast
        message="Please complete the required fields, full name and email address."
        isOpen={showInputErrorToast}
        hasError={true}
        onClose={() => setShowInputErrorToast(false)}
        duration={4000}
      />
    </FormLayout>
  );
};

export default KeyAgentSignUp;
